import { useLocation, useNavigate } from "react-router-dom";
import React, { useState, useRef, useEffect } from "react";
import { api } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import CustomStatusFilterComponent from "./components/excelStatusFilter";
import Header from "@/components/header";
import { AG_GRID_LOCALE_HK } from "@ag-grid-community/locale";
import { AgGridReact } from "ag-grid-react";
import { themeQuartz } from "ag-grid-community"; // 从 ag-grid-community 导入
import { colorSchemeDarkWarm } from "ag-grid-community";
import CryptoJS from "crypto-js";
import { toast } from "sonner";
import { AuthService } from "@/services/auth";
import { config } from "@/config/env";
import type { ColDef, ICellRendererParams } from "ag-grid-community";
import mdImage from "@/assets/search/ic-MarkDown.svg";
import { useTheme } from "@/contexts/ThemeContext";
import { useTranslation } from "@/locales";

// 引入 SheetJS
import * as XLSX from "xlsx";

// 深色主题风格
const themeDarkWarm = themeQuartz.withPart(colorSchemeDarkWarm).withParams({
  backgroundColor: "#1d1d1d",
});
localStorage.removeItem("isUploading");
let successfullyUploaded = 0;
// 新的文件数据结构（用于解析 Excel 行）
interface RowData {
  title: string;
  content: string;
  status: "待上传" | "已上传" | "内容重复" | "超出最大内容限制";
  md5: string;
  id: string; // 唯一标识每一行
}

// 状态翻译函数 - 移到组件内部以使用翻译函数
const translateStatus = (status: string, t: (key: string) => string): string => {
  switch (status) {
    case "待上传":
      return t("admin.pendingUpload");
    case "已上传":
      return t("admin.uploaded");
    case "内容重复":
      return t("admin.duplicateContent");
    case "超出最大内容限制":
      return t("admin.contentTooLong");
    case "上传失败":
      return t("admin.uploadFailed");
    default:
      return status;
  }
};

export default function uploadExcel() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // 判断当前主题是否是深色模式
  // const [isDarkMode, setIsDarkMode] = useState(false);
  const { resolvedTheme } = useTheme();
  const isDarkMode = resolvedTheme === "dark";

  const location = useLocation();
  const knowledgeId = location.state?.knowledgeId || "null"; // 获取传入的知识库ID
  const initialFile = location.state?.file || null; // 获取传入的单个文件

  const [files, setFiles] = useState<RowData[]>([]); // 改为 RowData 数组
  const [isCheckingMd5, setIsCheckingMd5] = useState(false);
  const deleteDialogRef = useRef<any>(null);
  const [DialogData, setDialogData] = useState({
    title: "",
    description: "",
    confirmText: "",
  });

  // 在组件挂载后设置翻译
  useEffect(() => {
    setDialogData({
      title: t("common.confirmDelete"),
      description: t("admin.deleteConfirmMessage"),
      confirmText: t("common.confirm"),
    });
  }, []);

  //loading上传进度
  const [uploadLength, setUploadLength] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // AgGrid 列配置
  const columnDefs: ColDef[] = [
    {
      headerName: t("file.fileName"),
      field: "title",
      flex: 1,
      minWidth: 200,
      maxWidth: 600,
      tooltipField: "title",
      cellRenderer: (params: ICellRendererParams) => {
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              width: "100%",
              height: "100%",
            }}
          >
            {/* 固定宽度的图标容器 */}
            <div
              style={{
                width: "24px",
                minWidth: "24px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginRight: "8px",
              }}
            >
              <img src={mdImage} style={{ width: "20px", height: "auto" }} alt="PDF" />
            </div>

            {/* 可省略的文本内容 */}
            <div
              style={{
                flex: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              <span>{params.value}</span>
            </div>
          </div>
        );
      },
      filterValueGetter: (params) => {
        return params.data.title;
      },
    },
    {
      headerName: t("common.description"),
      field: "content",
      flex: 2,
      maxWidth: 800,
      tooltipField: "content",
      cellRenderer: (params: ICellRendererParams) => {
        return (
          <div
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            <span>{params.value}</span>
          </div>
        );
      },
      filterValueGetter: (params) => {
        return params.data.content;
      },
    },
    {
      headerName: t("admin.status"),
      // sortable: true,
      field: "status",
      filter: CustomStatusFilterComponent, // 使用自定义过滤器
      cellRenderer: (params: ICellRendererParams) => {
        let style = {};
        if (params.value === "待上传") {
          style = { color: "#F08800" };
        } else if (params.value === "已上传") {
          style = { color: "#00b83d" };
        } else if (params.value === "内容重复") {
          style = { color: "#F92923" };
        } else if (params.value === "超出最大内容限制") {
          style = { color: "#F92923" };
        }
        return <span style={style}>{translateStatus(params.value, t)}</span>;
      },
      flex: 1,
    },
    {
      headerName: t("admin.actions"),
      cellRenderer: (params: ICellRendererParams) => (
        <button onClick={() => handleDeleteRow(params.data.id)} style={{ color: "#F92923", cursor: "pointer" }}>
          {t("common.delete")}
        </button>
      ),
      width: 80,
    },
  ];

  const defaultColDef = {
    flex: 1,
    minWidth: 100,
    maxWidth: 400,
    sortable: false,
    cellStyle: {
      display: "flex",
      alignItems: "center",
      userSelect: "text",
      WebkitUserSelect: "text",
      MozUserSelect: "text",
    },
  };
  useEffect(() => {
    const handleAbortUpload = () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort(); // 取消所有请求
        localStorage.setItem("isUploading", "false");
      }
    };

    window.addEventListener("abortUpload", handleAbortUpload);
    //当组件卸载时，仍在进行的请求会被取消，避免更新已卸载组件的状态
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      localStorage.removeItem("isUploading");
      window.removeEventListener("abortUpload", handleAbortUpload);
    };
  }, []);

  // 处理从上一步传入的文件
  useEffect(() => {
    if (initialFile) {
      processExcelFile(initialFile);
    }
  }, []);

  // 处理 Excel 文件
  const processExcelFile = async (file: File) => {
    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        // 读取为对象数组格式，自动使用第一行作为列名
        const jsonData: any[] = XLSX.utils.sheet_to_json(worksheet);

        // 检查是否存在 title 和 content 列
        if (jsonData.length > 0) {
          const firstRow = jsonData[0];
          if (!("title" in firstRow) || !("content" in firstRow)) {
            // 尝试查找可能的列名（不区分大小写）
            const keys = Object.keys(firstRow);
            const titleKey = keys.find((key) => key.toLowerCase() === "title");
            const contentKey = keys.find((key) => key.toLowerCase() === "content");

            if (!titleKey || !contentKey) {
              // toast.error(t('excelMustContainTitleAndContent'));
              return;
            }
          }
        }

        const contentSet = new Set<string>();
        const rowDataList: RowData[] = [];

        // 处理所有数据行
        for (const row of jsonData) {
          // 获取 title 和 content 字段（处理可能的大小写问题）
          let title = "";
          let content = "";

          // 查找 title 和 content 字段
          for (const key in row) {
            if (key.toLowerCase() === "title") {
              title = row[key];
            } else if (key.toLowerCase() === "content") {
              content = row[key];
            }
          }

          // 检查 title 或 content 是否为空
          if (!title || !content) {
            continue;
          }

          // 检查 title 或 content 是否为空字符串（去除空格后）
          if (title.trim() === "" || content.trim() === "") {
            continue;
          }

          // 检查 content 是否超长
          if (content.length > 4096) {
            rowDataList.push({
              title,
              content,
              status: "超出最大内容限制",
              md5: "",
              id: `row-${rowDataList.length}`,
            });
            continue;
          }

          // 检查当前列表中是否有相同 content
          if (contentSet.has(content)) {
            rowDataList.push({
              title,
              content,
              status: "内容重复",
              md5: "",
              id: `row-${rowDataList.length}`,
            });
            continue;
          }

          contentSet.add(content);

          // 默认状态为"待上传"
          rowDataList.push({
            title,
            content,
            status: "待上传",
            md5: "",
            id: `row-${rowDataList.length}`,
          });
        }

        // 设置初始数据
        setFiles(rowDataList);

        // 不再立即调用 MD5 查重，只在用户点击确认上传时才进行
      } catch (error) {
        console.error(t("admin.processExcelFailed"), error);
        toast.error(t("admin.pleaseSelectExcelFile"));
      }
    };

    reader.onerror = () => {
      toast.error(t("admin.fileReadFailed"));
    };

    reader.readAsArrayBuffer(file);
  };

  // 对每条数据进行 MD5 查重
  const checkMd5ForRows = async (rowDataList: RowData[]) => {
    setIsCheckingMd5(true);

    try {
      // 创建新的 AbortController
      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;

      // 过滤出需要查重的数据（排除已标记为重复或超限的数据）
      const rowsToCheck = rowDataList.filter((row) => row.status === "待上传");

      // 逐条检查 MD5
      for (const row of rowsToCheck) {
        // 检查请求是否已被取消
        if (signal.aborted) {
          break;
        }

        try {
          // 计算 content 的 MD5
          const md5 = CryptoJS.MD5(row.content).toString();

          // 调用后端 MD5 查重接口
          const response = await api.get("/v1/admin/knowledge/file/md5", {
            params: {
              knowledgeId,
              md5,
            },
            signal, // 传递 signal 以支持取消请求
          });

          // 检查响应并更新状态
          if (response && response.fileId > 0) {
            setFiles((prevFiles) => prevFiles.map((f) => (f.id === row.id ? { ...f, status: "内容重复", md5 } : f)));
          } else {
            // 如果不存在，更新 md5 值
            setFiles((prevFiles) => prevFiles.map((f) => (f.id === row.id ? { ...f, md5 } : f)));
          }
        } catch (error: any) {
          // 忽略取消请求的错误
          if (error.name === "AbortError") {
            console.log(t("common.requestCancelled"));
            break;
          }

          console.error(t("admin.md5CheckFailed"), error);
          // 即使查重失败，也保持原状态继续处理
        }
      }
    } finally {
      setIsCheckingMd5(false);
    }
  };

  // 删除某一行
  const handleDeleteRow = (id: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  };

  // 上传处理
  const handleUpload = async () => {
    let toUpload = files.filter((f) => f.status === "待上传");
    setUploadLength(toUpload.length);

    if (toUpload.length === 0) {
      toast.error(t("admin.noValidFilesToUpload"));
      return;
    }

    try {
      // 先进行 MD5 查重并逐条上传
      setIsCheckingMd5(true);

      // 创建新的 AbortController
      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;

      // 过滤出需要查重的数据（只查重状态为"待上传"的数据）
      const rowsToCheck = [...toUpload]; // 创建副本避免修改原数组
      //let successfullyUploaded = 0; // 记录成功上传的数量

      // 逐条检查 MD5 并立即上传
      for (const row of rowsToCheck) {
        // 检查请求是否已被取消
        if (signal.aborted) {
          break;
        }

        try {
          // 计算 content 的 MD5
          const md5 = CryptoJS.MD5(row.content).toString();

          // 调用后端 MD5 查重接口
          const response = await api.get("/v1/admin/knowledge/file/md5", {
            params: {
              knowledgeId,
              md5,
            },
            signal, // 传递 signal 以支持取消请求
          });

          // 检查响应并更新状态
          if (response && response.fileId > 0) {
            // 如果存在重复，更新状态为"内容重复"
            setFiles((prevFiles) => prevFiles.map((f) => (f.id === row.id ? { ...f, status: "内容重复", md5 } : f)));
            // 显示重复提示
            // toast.info(`文件 "${row.title}" 内容重复，已跳过上传 (1/${toUpload.length})`);
          } else {
            // 如果不存在重复，先更新 md5 值
            setFiles((prevFiles) => prevFiles.map((f) => (f.id === row.id ? { ...f, md5 } : f)));

            // 立即上传该条数据
            try {
              localStorage.setItem("isUploading", "true");
              const body = {
                knowledgeId: Number(knowledgeId),
                title: row.title,
                content: row.content,
              };
              // 设置content-type为x-www-form-urlencoded
              await api.post("/v1/admin/knowledge/file/markdown/upload", new URLSearchParams(body as any).toString(), {
                headers: {
                  "Content-Type": "application/x-www-form-urlencoded",
                },
              });

              // 更新状态为已上传
              setFiles((prev) => prev.map((f) => (f.id === row.id ? { ...f, status: "已上传" } : f)));

              successfullyUploaded++;
              // 显示上传进度提示
              // toast.success(`文件 "${row.title}" 上传成功 (${successfullyUploaded}/${toUpload.length})`);
            } catch (uploadError) {
              console.error("上传失败:", uploadError);
              setFiles((prev) => prev.map((f) => (f.id === row.id ? { ...f, status: "上传失败" } : f)));
              // 显示失败提示
              // toast.error(`文件 "${row.title}" 上传失败 (${successfullyUploaded + 1}/${toUpload.length})`);
            }
          }
        } catch (error: any) {
          // 忽略取消请求的错误
          if (error.name === "AbortError") {
            console.log(t("common.requestCancelled"));
            break;
          }

          console.error(t("admin.md5CheckFailed"), error);
          // 即使查重失败，也保持原状态继续处理下一条
          // toast.error(`文件 "${row.title}" 查重失败，跳过上传`);
        }
      }

      // 全部处理完成后显示总结信息
      if (successfullyUploaded > 0) {
        toast.success(`${t("admin.uploadComplete")}, ${t("admin.total")} ${successfullyUploaded}/${toUpload.length} ${t("admin.itemsUploadedSuccessfully")}`);
      } else if (successfullyUploaded === 0 && toUpload.length > 0) {
        toast.info(t("admin.noFilesUploaded"));
      }

      // 检查是否全部处理完成（包括重复和失败的）
      const processedFiles = files.filter((f) => f.status === "已上传" || f.status === "内容重复" || f.status === "上传失败" || f.status === "超出最大内容限制");

      if (processedFiles.length === files.length) {
        // 全部处理完成后返回上一页
        setTimeout(() => {
          // navigate(-1);
        }, 2000); // 等待2秒让用户看到最终结果
      }
    } catch (error) {
      console.error(t("admin.uploadError"), error);
      toast.error(t("admin.uploadError"));
    } finally {
      setIsCheckingMd5(false);
      localStorage.removeItem("isUploading");
    }
  };

  // 返回按钮
  const goBack = () => {
    if (localStorage.getItem("isUploading") === "true") {
      toast.info(t("admin.fileUploadingPleaseWait"));
      return;
    }
    navigate(-1);
  };

  // 排序处理函数,处理图标
  const onSortChanged = (event: any) => {
    const columnState = event.api.getColumnState();
    const sortedColumn = columnState.find((col: any) => col.sort);

    if (sortedColumn) {
      // 当有列被排序时，移除提示图标样式
      const style = document.getElementById("grid-sort-icons");
      if (style) {
        style.innerHTML = `.ag-header-cell[col-id="status"] .ag-header-cell-label::after {
                    display: none !important;
                }
            `;
      }
    } else {
      // 当没有列被排序时，恢复提示图标样式
      const style = document.getElementById("grid-sort-icons");
      if (style) {
        style.innerHTML = `.ag-header-cell[col-id="status"] .ag-header-cell-label::after {
                    content: " ⇅";
                    font-size: 14px;
                    opacity: 0.7;
                    margin-left: 5px;
                }
            `;
      }
    }
  };
  return (
    <main className="pl-4 pr-4 py-12 pt-0 dark:bg-[#1d1d1d] bg-[#ffffff]" style={{ width: "100%" }}>
      {/*<style id="grid-sort-icons">
                {`.ag-header-cell[col-id="status"] .ag-header-cell-label::after{
                content: " ⇅";
                font-size: 14px;
                opacity: 0.7;
                margin-left: 5px;
            }`}
            </style>*/}
      <Header title={t("admin.fileUpload")} />
      <div className="space-y-4">
        <div className="flex justify-between items-center mx-auto space-y-4 mt-5">
          <h2 className="text-xl font-bold mb-0">
            <span className="whitespace-nowrap" style={{ fontSize: "16px", cursor: "pointer" }} onClick={goBack}>
              <span style={{ color: "#9c9c9c" }}>{t("admin.fileCollection")} / </span>
              {t("admin.batchUpload")}
            </span>
            <span className={"ml-2 text-[#9c9c9c] text-sm"}>{files.length > 0 && `(${t("admin.total")}${files.length}${t("admin.items")})`}</span>
          </h2>
          <div className="space-x-2"></div>

          <div className="space-x-2">
            <Button onClick={() => fileInputRef.current?.click()} disabled={isCheckingMd5 || files.length > 0}>
              + {t("admin.scanFile")}
            </Button>
            <input
              type="file"
              accept=".xlsx,.xls"
              ref={fileInputRef}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) processExcelFile(file);
              }}
              multiple={false}
              style={{ display: "none" }}
            />
            <Button variant="outline" onClick={goBack}>
              {t("common.back")}
            </Button>
          </div>
          {/* <div className="space-x-2">
                        <Button onClick={openFileChooser} disabled={isCheckingMd5|| files.length>0}>+ 扫描文件</Button>
                        <input
                            type="file"
                            accept=".xlsx,.xls"
                            ref={fileInputRef}
                            onChange={handleFileSelect}
                            multiple={false}
                            style={{ display: 'none' }}
                        />
                    </div>*/}
        </div>

        <div className="ag-theme-alpine" style={{ height: "70vh", width: "100%" }}>
          <AgGridReact
            theme={isDarkMode ? themeDarkWarm : themeQuartz} //themeQuartz 默认主题风格
            columnDefs={columnDefs}
            rowData={files}
            defaultColDef={defaultColDef}
            rowHeight={50}
            localeText={AG_GRID_LOCALE_HK}
            onSortChanged={onSortChanged} // 添加排序变化监听
            loadingOverlayComponent={() => (
              <div className="ag-custom-loading">
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    flexDirection: "column",
                  }}
                >
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mb-2"></div>
                  <span>
                    {t("status.uploading")}({successfullyUploaded}/{uploadLength}){t("admin.items")}
                  </span>
                </div>
              </div>
            )}
            loadingOverlayComponentParams={{
              loadingMessage: t("status.uploading"),
            }}
            // 控制何时显示加载状态
            loading={isCheckingMd5 && files.length > 10}
          />
        </div>

        <div className="space-x-2">
          <Button onClick={handleUpload} disabled={files.length === 0 || isCheckingMd5}>
            {isCheckingMd5 ? t("status.uploading") : t("admin.confirmUpload")}
          </Button>
        </div>
      </div>

      {/* 确认弹窗 */}
      <ConfirmDialog ref={deleteDialogRef} title={DialogData.title} description={DialogData.description} confirmText={DialogData.confirmText} onConfirm={() => {}} />
    </main>
  );
}
